import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  CircularProgress,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useSubscription } from 'AppRoutes/context/SubscriptionContext';
import SubscriptionOrderSummary from 'components/SubscriptionOrderSummary';
import { useShippingAbandonTracking } from 'hooks/useShippingAbandonTracking';
import { analyticsService } from 'services/analytics/analyticsService';
import { auth } from 'services/firebase/client';
import {
  getMelhorEnvioQuote,
  type ShippingQuoteResponse,
} from 'services/shippingService';
import {
  parsePlanPrice,
  roundCurrency,
  sumCurrency,
} from 'utils/currency';
import { getPackQuantity } from 'utils/subscriptionPlanFeatures';
import {
  buildPlanKey,
  clearSubscriptionPendingAdvance,
  formatCep,
  loadSubscriptionPendingAdvance,
  loadSubscriptionShippingPreview,
  saveSubscriptionShippingPreview,
} from 'utils/subscriptionShippingPreview';

import styles from './styles';

const hasDeliveryAddress = (data: {
  cep?: string;
  address?: { street?: string; city?: string };
  number?: string;
}): boolean => {
  const cepDigits = data.cep?.replace(/\D/g, '') ?? '';
  return (
    cepDigits.length === 8 &&
    Boolean(data.address?.street || data.address?.city) &&
    Boolean(data.number?.trim())
  );
};

const SubscriptionReview = () => {
  const { selectedPlan, personalData, updateSubscription } = useSubscription();
  const navigate = useNavigate();

  const [quantity] = useState(selectedPlan?.quantity || 1);
  const [cep, setCep] = useState('');
  const [shippingValue, setShippingValue] = useState<number | null>(null);
  const [shippingPreview, setShippingPreview] =
    useState<ShippingQuoteResponse | null>(null);
  const [cepLoading, setCepLoading] = useState(false);
  const [cepError, setCepError] = useState('');

  const planKey = useMemo(() => buildPlanKey(selectedPlan), [selectedPlan]);

  const subscriptionPlanLabel = selectedPlan
    ? `${selectedPlan.label} - ${selectedPlan.type}`
    : undefined;

  const { markAdvanceClicked } = useShippingAbandonTracking(
    'subscription_review',
    shippingValue,
    subscriptionPlanLabel,
  );

  const subtotal = useMemo(
    () => roundCurrency(parsePlanPrice(selectedPlan?.price) * quantity),
    [quantity, selectedPlan?.price],
  );

  const total = sumCurrency(subtotal, shippingValue ?? 0);

  const pack = {
    title: `${selectedPlan?.label} - ${selectedPlan?.type}`,
    originalPrice: `R$ ${selectedPlan?.originalPrice}`,
    promotionalPrice: `R$ ${selectedPlan?.price}`,
  };

  const applyShippingQuote = useCallback(
    (cepDigits: string, quote: ShippingQuoteResponse) => {
      const roundedPrice = roundCurrency(quote.price);

      setCep(formatCep(cepDigits));
      setShippingValue(roundedPrice);
      setShippingPreview({ ...quote, price: roundedPrice });

      if (selectedPlan) {
        saveSubscriptionShippingPreview({
          planKey: buildPlanKey(selectedPlan),
          cep: cepDigits,
          quote,
        });
      }
    },
    [selectedPlan],
  );

  useEffect(() => {
    analyticsService.trackPageView('/subscription', 'Revisão da Assinatura');
  }, []);

  useEffect(() => {
    if (!selectedPlan) {
      navigate('/');
    }
  }, [navigate, selectedPlan]);

  useEffect(() => {
    if (!selectedPlan || !planKey) {
      return;
    }

    const storedPreview = loadSubscriptionShippingPreview(planKey);
    if (storedPreview) {
      applyShippingQuote(storedPreview.cep, storedPreview.quote);
    }
  }, [applyShippingQuote, planKey, selectedPlan]);

  useEffect(() => {
    if (!selectedPlan || !planKey || !auth.currentUser) {
      return;
    }

    const pendingAdvance = loadSubscriptionPendingAdvance(planKey);
    if (!pendingAdvance) {
      return;
    }

    updateSubscription({
      ...selectedPlan,
      quantity: pendingAdvance.quantity,
      subtotal: pendingAdvance.subtotal,
      total: pendingAdvance.subtotal,
    });
    clearSubscriptionPendingAdvance();
  }, [planKey, selectedPlan, updateSubscription]);

  const handleFetchCep = useCallback(async () => {
    const cepDigits = cep.replace(/\D/g, '');
    if (cepDigits.length !== 8) {
      setCepError('Digite um CEP válido (8 dígitos).');
      return;
    }

    const bottleCount = getPackQuantity(
      selectedPlan?.label ?? selectedPlan?.pack ?? '',
    );

    if (bottleCount <= 0) {
      setCepError('Plano inválido para cálculo de frete.');
      return;
    }

    setCepError('');
    setCepLoading(true);

    analyticsService.trackShippingQuoteRequested({
      source: 'subscription_review',
      cepPrefix: cepDigits,
      subscriptionPlan: subscriptionPlanLabel,
    });

    try {
      const quote = await getMelhorEnvioQuote({
        destinationPostalCode: cepDigits,
        bottleCount,
        insuranceValue: subtotal,
      });
      applyShippingQuote(cepDigits, quote);
      analyticsService.trackShippingQuoteSuccess({
        source: 'subscription_review',
        cepPrefix: cepDigits,
        shippingPrice: quote.price,
        subscriptionPlan: subscriptionPlanLabel,
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Não foi possível calcular o frete. Tente novamente.';

      analyticsService.trackShippingQuoteError({
        source: 'subscription_review',
        cepPrefix: cepDigits,
        subscriptionPlan: subscriptionPlanLabel,
        errorMessage,
      });

      setCepError(errorMessage);
      setShippingValue(null);
      setShippingPreview(null);
    } finally {
      setCepLoading(false);
    }
  }, [
    applyShippingQuote,
    cep,
    selectedPlan?.label,
    selectedPlan?.pack,
    subtotal,
    subscriptionPlanLabel,
  ]);

  const handleNext = useCallback(() => {
    const user = auth.currentUser;
    const hadShippingQuote = shippingValue !== null;

    if (selectedPlan) {
      updateSubscription({
        ...selectedPlan,
        quantity,
        subtotal,
        total: subtotal,
      });
    }

    const redirectTo = user
      ? hasDeliveryAddress(personalData)
        ? '/shipping'
        : '/subscription/address'
      : '/subscription/account';

    markAdvanceClicked();
    analyticsService.trackCta('subscription_review_continue', 'Continuar', {
      redirect_to: redirectTo,
      subscription_plan: subscriptionPlanLabel ?? null,
    });
    analyticsService.trackShippingAdvanceClick({
      source: 'subscription_review',
      isAuthenticated: Boolean(user),
      hadShippingQuote,
      shippingPrice: shippingValue ?? undefined,
      subscriptionPlan: subscriptionPlanLabel,
      redirectTo,
    });

    navigate(redirectTo);
  }, [
    markAdvanceClicked,
    navigate,
    personalData,
    quantity,
    selectedPlan,
    shippingValue,
    subtotal,
    subscriptionPlanLabel,
    updateSubscription,
  ]);

  return (
    <Box sx={styles.container}>
      <Grid container spacing={4} sx={styles.gridContainer}>
        <Grid item xs={12} sm={6}>
          <Card sx={styles.card}>
            <CardContent>
              <Box sx={styles.itemContainer}>
                <Box>
                  <Typography variant="h5" sx={styles.title}>
                    {pack.title}
                  </Typography>
                  <Typography variant="h6" sx={styles.originalPrice}>
                    {pack.originalPrice} por
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                    <Typography variant="h4" sx={styles.promotionalPrice}>
                      {pack.promotionalPrice}
                    </Typography>
                    <Typography variant="body1" sx={styles.promotionalPrice}>
                      {' '}
                      / mês
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6}>
          <SubscriptionOrderSummary
            subtotal={subtotal}
            shippingValue={shippingValue}
            shippingPreview={shippingPreview}
            total={total}
            onAdvance={handleNext}
            topSlot={
              <Box sx={{ mt: 2, mb: 2 }}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Prévia do frete pelo CEP (opcional — valor estimado)
                </Typography>
                <Box
                  sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}
                >
                  <TextField
                    placeholder="00000-000"
                    value={cep}
                    onChange={e => {
                      const v = e.target.value.replace(/\D/g, '').slice(0, 8);
                      setCep(
                        v.length > 5 ? `${v.slice(0, 5)}-${v.slice(5)}` : v,
                      );
                      setShippingValue(null);
                      setShippingPreview(null);
                      setCepError('');
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        void handleFetchCep();
                      }
                    }}
                    error={!!cepError}
                    helperText={cepError}
                    size="small"
                    inputProps={{ maxLength: 9 }}
                    sx={{ flex: 1 }}
                  />
                  <Button
                    variant="outlined"
                    onClick={() => {
                      void handleFetchCep();
                    }}
                    disabled={cepLoading}
                    sx={{ minWidth: 120 }}
                  >
                    {cepLoading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      'Calcular'
                    )}
                  </Button>
                </Box>
              </Box>
            }
          />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 4 }}>
        <Button variant="outlined" color="primary" onClick={() => navigate(-1)}>
          Voltar
        </Button>
      </Box>
    </Box>
  );
};

export default SubscriptionReview;
